import type { FileUIPart } from "ai";
import React from "react";
import { isPhotoPart } from "@/features/tessa/components/utils/chatPhotos";
import type { TessaResponseMessages } from "@/features/tessa/types";

/**
 * The messages of the conversation in progress. Photos only live here: they're
 * sent to tessa for the turn they come with and aren't part of the history.
 */
export const TessaLiveMessagesContext = React.createContext<
  TessaResponseMessages[]
>([]);

// The photos found for a response while its turn was live, so they're still there after the turn is saved or
// the user switches chats and back (until the page reloads)
const photosSeen = new Map<string, FileUIPart[]>();

/** The photos the user sent in the message a response answers */
export const usePhotosAnswered = (messageId?: string): FileUIPart[] => {
  const messages = React.useContext(TessaLiveMessagesContext);

  return React.useMemo(() => {
    const index = messages.findIndex((message) => message.id === messageId);
    if (index < 0) return (messageId && photosSeen.get(messageId)) || [];

    for (let i = index - 1; i >= 0; i--) {
      if (messages[i].role === "user") {
        const photos = messages[i].parts.filter(isPhotoPart);
        if (messageId && photos.length > 0) photosSeen.set(messageId, photos);
        return photos;
      }
    }
    return [];
  }, [messages, messageId]);
};
